// Which printer serves a KDS category's ticket.
//
// Its own module, not a helper inside PrintingUtils, because two very
// different surfaces have to agree on the answer: PrintingUtils.printCategoryTicket
// ROUTES the ticket, and StationSettingsPrinter TELLS THE SHOP where it will
// go. A settings sheet that describes routing it does not share is how the
// "Falls back to kitchen" label came to be printed beside categories that
// printed nothing at all (QC 2026-09-09). PrintingUtils pulls in react-to-print,
// the Electron host bridge and the axios client, so the settings sheet cannot
// import it — hence a leaf module with no imports of its own.

// The printer roles that could serve `category`, best first.
//
//   1. The trimmed category name VERBATIM. This is the key the rest of the
//      system uses: StationSettingsPrinter saves a per-category printer with
//      `role` set to the raw category name (roleOptionsFor), and the backend
//      validates a ticket role with `Category.name == role`
//      (printing._is_valid_printer_role) — a case-sensitive comparison. So
//      the raw name is the ONLY spelling that can reach a per-category
//      printer, which is why this is not lowercased.
//   2. Its lowercase form, when that differs. Legacy tenants have printers
//      with role 'kitchen'/'bar' (StationSettingsPrinter.LEGACY_ROLES), and
//      a category named "Bar" must still find the 'bar' printer.
//   3. 'kitchen' — the universal fallback, and for most shops the only
//      ticket printer there is.
//
// Deduplicated, so a category already named 'kitchen' yields exactly
// ['kitchen'] and cannot be tried twice.
export const ticketRoleCandidates = (category) => {
    const raw = String(category ?? '').trim();
    const candidates = [];
    if (raw) candidates.push(raw);
    const lower = raw.toLowerCase();
    if (lower && lower !== raw) candidates.push(lower);
    if (!candidates.includes('kitchen')) candidates.push('kitchen');
    return candidates;
};

// The active printer a category's ticket would actually come out of, or null
// when the chain runs out and nothing prints. `assignedId`, when given, is
// this device's station-config assignment for the category and wins outright
// — the same precedence resolveDevicePrinter applies at print time.
export const printerForCategory = (printers, category, assignedId = null) => {
    const list = printers || [];
    if (assignedId) {
        const assigned = list.find((p) => p.id === assignedId && p.is_active);
        if (assigned) return assigned;
    }
    for (const role of ticketRoleCandidates(category)) {
        const match = list.find((p) => p.role === role && p.is_active);
        if (match) return match;
    }
    return null;
};
